import { useMutation, useQueryClient } from '@tanstack/react-query';
import { companyApi, TwoFactorSettingsData } from 'shared/services/companyApi';
import { queryKeys } from '../queryConfig';

interface UpdateTwoFactorSettingsParams {
  companyId: string;
  settings: TwoFactorSettingsData;
}

/**
 * Hook to update 2FA settings for a company
 *
 * @example
 * ```tsx
 * const updateSettings = useUpdateTwoFactorSettings();
 *
 * const handleSave = () => {
 *   updateSettings.mutate({
 *     companyId: 'company-123',
 *     settings: {
 *       enabled: true,
 *       requiredRoles: ['COMPANY_ADMIN', 'CLIENT_APPROVER'],
 *       allowedMethods: ['authenticator', 'email'],
 *       enforceStrongestMethod: false,
 *       gracePeriodDays: 7,
 *     }
 *   });
 * };
 * ```
 */
export const useUpdateTwoFactorSettings = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ companyId, settings }: UpdateTwoFactorSettingsParams) => {
      const response = await companyApi.updateTwoFactorSettings(companyId, settings);

      if (!response.success) {
        throw new Error(response.error || 'Error al actualizar configuración 2FA');
      }

      return response;
    },
    onSuccess: (_data, variables) => {
      // Invalidate settings and compliance queries for this company
      queryClient.invalidateQueries({
        queryKey: queryKeys.twoFactor.settings(variables.companyId),
      });
      queryClient.invalidateQueries({
        queryKey: queryKeys.twoFactor.compliance(variables.companyId),
      });
      // Also invalidate company detail in case settings are embedded
      queryClient.invalidateQueries({
        queryKey: queryKeys.companies.detail(variables.companyId),
      });
    },
  });
};
